import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, AlertTriangle, ArrowRight, Tag, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { formatGBP } from "../components/common/StockBadge";

export default function Basket() {
  const { basket, updateQty, removeItem, clearBasket, applyVoucher, removeVoucher } = useShop();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const items = basket.items || [];
  const discounts = basket.discounts || [];
  const rrpTotal = basket.rrp_total ?? basket.subtotal;
  const rrpSavings = basket.rrp_savings || 0;
  const combinedSavings = basket.combined_savings ?? (basket.total_savings || 0);
  const estimated = basket.estimated_total ?? basket.subtotal;
  const promoMessages = basket.promotion_messages || [];

  const [voucher, setVoucher] = useState("");
  const [applying, setApplying] = useState(false);

  const onApply = async () => {
    if (!voucher.trim()) return;
    setApplying(true);
    const res = await applyVoucher(voucher.trim());
    setApplying(false);
    if (res.ok) { toast.success(res.message || "Coupon applied"); setVoucher(""); }
    else toast.error(res.message || "Coupon could not be applied.");
  };

  const onRemoveVoucher = async () => { await removeVoucher(); toast.message("Coupon removed"); };

  return (
    <div className="min-h-screen bg-ink py-10" data-testid="basket-page">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h1 className="font-playfair text-3xl sm:text-4xl text-white flex items-center gap-3"><ShoppingBag className="h-7 w-7 text-gold" /> Your Basket</h1>

        {items.length === 0 ? (
          <div className="mt-10 text-center" data-testid="basket-empty">
            <p className="text-white/60">Your basket is empty.</p>
            <Button onClick={() => navigate("/fireworks")} className="cf-gold-btn font-semibold mt-6">Browse Fireworks</Button>
          </div>
        ) : (
          <div className="mt-8 grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-3">
              {items.map((it) => (
                <div key={it.product_id} data-testid={`basket-item-${it.product_id}`} className={`flex items-center gap-4 rounded-xl border p-3 ${it.unavailable ? "border-firered/40 bg-firered/5" : "border-white/10 bg-white/5"}`}>
                  <div className="h-20 w-20 rounded-lg overflow-hidden bg-black/30 shrink-0">
                    {it.image ? <img src={it.image} alt={it.name} className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center text-white/20"><ShoppingBag className="h-6 w-6" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={it.slug ? `/product/${it.slug}` : "#"} className="text-white font-medium hover:text-gold truncate block">{it.name}</Link>
                    {it.unavailable ? (
                      <p className="text-firered text-xs flex items-center gap-1 mt-1"><AlertTriangle className="h-3.5 w-3.5" /> No longer available — please remove</p>
                    ) : (
                      <>
                        <p className="text-gold text-sm mt-0.5">{formatGBP(it.retail_price)}{!it.in_stock && <span className="text-amber text-xs ml-2">Currently unavailable</span>}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button data-testid={`qty-dec-${it.product_id}`} onClick={() => updateQty(it.product_id, it.qty - 1)} className="h-7 w-7 grid place-items-center rounded border border-white/20 text-white hover:bg-white/10"><Minus className="h-3.5 w-3.5" /></button>
                          <span className="text-white w-8 text-center" data-testid={`qty-${it.product_id}`}>{it.qty}</span>
                          <button data-testid={`qty-inc-${it.product_id}`} onClick={() => updateQty(it.product_id, it.qty + 1)} className="h-7 w-7 grid place-items-center rounded border border-white/20 text-white hover:bg-white/10"><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="text-right">
                    {!it.unavailable && <p className="text-white font-semibold">{formatGBP(it.line_total)}</p>}
                    <button data-testid={`remove-${it.product_id}`} onClick={() => removeItem(it.product_id)} className="text-white/40 hover:text-firered mt-2"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              <button onClick={clearBasket} data-testid="clear-basket-btn" className="text-white/50 hover:text-firered text-sm">Clear basket</button>
            </div>

            <div className="lg:col-span-1">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sticky top-24" data-testid="basket-summary">
                <h2 className="font-playfair text-xl text-white">Reservation Summary</h2>
                {rrpSavings > 0 && (
                  <div className="mt-4 flex justify-between text-white/60 text-sm"><span>Total RRP</span><span className="line-through decoration-firered" data-testid="basket-rrp-total">{formatGBP(rrpTotal)}</span></div>
                )}
                <div className="mt-2 flex justify-between text-white/80"><span>{rrpSavings > 0 ? "Our price" : "Subtotal"}</span><span data-testid="basket-subtotal">{formatGBP(basket.subtotal)}</span></div>

                {rrpSavings > 0 && (
                  <div className="mt-3 flex justify-between text-sm" data-testid="basket-rrp-savings">
                    <span className="text-emerald-400 flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> Saving vs RRP</span>
                    <span className="text-emerald-400 whitespace-nowrap">− {formatGBP(rrpSavings)}</span>
                  </div>
                )}

                {discounts.length > 0 && (
                  <div className="mt-2 space-y-1.5" data-testid="basket-discounts">
                    {discounts.map((d) => (
                      <div key={d.promotion_id || d.label} className="flex justify-between text-sm" data-testid={`discount-${d.promotion_id || d.label}`}>
                        <span className="text-emerald-400 flex items-center gap-1.5 min-w-0" title={`${d.label}${d.is_voucher ? " (coupon)" : ""}`}>
                          <Tag className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{d.label}{d.is_voucher ? " (coupon)" : ""}</span>
                        </span>
                        <span className="text-emerald-400 whitespace-nowrap">− {formatGBP(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {combinedSavings > 0 && (
                  <div className="mt-3 flex justify-between text-sm text-emerald-400 font-semibold border-t border-white/10 pt-3" data-testid="basket-total-savings">
                    <span>Total savings</span><span>− {formatGBP(combinedSavings)}</span>
                  </div>
                )}

                {promoMessages.length > 0 && (
                  <div className="mt-4 space-y-2" data-testid="basket-promo-messages">
                    {promoMessages.map((m, i) => (
                      <div key={m.promotion_id || i} data-testid={`promo-message-${i}`}
                        className={`rounded-lg border px-3 py-2 text-xs flex items-start gap-2 ${m.tone === "success" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-gold/30 bg-gold/5 text-gold"}`}>
                        <Tag className="h-3.5 w-3.5 shrink-0 mt-0.5" /> <span>{m.message}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Voucher */}
                <div className="mt-4 border-t border-white/10 pt-4">
                  {basket.voucher_code && basket.voucher_valid ? (
                    <div className="flex items-center justify-between rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2" data-testid="applied-voucher">
                      <span className="text-emerald-300 text-sm flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> {basket.voucher_code}</span>
                      <button data-testid="remove-voucher-btn" onClick={onRemoveVoucher} className="text-emerald-300/70 hover:text-firered"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <div>
                      <label className="text-xs text-white/50">Have a coupon code?</label>
                      <div className="mt-1.5 flex gap-2">
                        <Input data-testid="voucher-input" value={voucher} onChange={(e) => setVoucher(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && onApply()} placeholder="e.g. FIREWORKS10" className="bg-black/30 border-white/15 text-white placeholder:text-white/30 h-10" />
                        <Button data-testid="apply-voucher-btn" onClick={onApply} disabled={applying || !voucher.trim()} className="cf-gold-btn font-semibold h-10 shrink-0">Apply</Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex justify-between text-white text-lg font-semibold border-t border-white/10 pt-4">
                  <span>Estimated total</span><span data-testid="basket-estimated-total">{formatGBP(estimated)}</span>
                </div>
                <p className="mt-1 text-xs text-white/40">Estimated total payable in store. No payment is taken online — you pay when you collect.</p>

                {basket.issues?.length > 0 && <p className="mt-3 text-xs text-amber flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Some items need attention before checkout.</p>}
                <Button data-testid="checkout-btn" onClick={() => navigate("/checkout")} className="cf-gold-btn font-semibold w-full mt-6 gap-2 h-12">
                  {isAuthenticated ? "Continue to Reservation" : "Sign in to Reserve"} <ArrowRight className="h-4 w-4" />
                </Button>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-white/40"><ShieldCheck className="h-3.5 w-3.5 text-gold" /> Reserve online, pay in store. Fireworks sold only to over-18s — ID may be required at collection.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
